import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";

import { logError } from "@up/data";

import FinalAgreement from "./FinalAgreement";
import { SubmitButton } from "./SubmitButton";

import type { MouseEvent, SyntheticEvent } from "react";

type Props = {
  isProcessing: boolean;
  setProcessing: (state: boolean) => void;
  successURL: string;
  submitButtonText: string;
};

export default function StripeForm({
  isProcessing,
  setProcessing,
  successURL,
  submitButtonText,
}: Props) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const stripe = useStripe()!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const elements = useElements()!;

  const [stripeError, setStripeError] = useState("");

  const handleStripeSubmit = async function (
    e?: SyntheticEvent<Element, Event>,
  ) {
    e?.preventDefault();

    setProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: successURL,
      },
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setStripeError(error.message!);
      setProcessing(false);
    } else {
      logError({ action: "STRIPE_UNKNOWN_ERROR", error });
      setStripeError("An unexpected error occurred.");
      setProcessing(false);
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleStripeSubmit}>
      <PaymentElement id="payment-element" />

      <br />

      {stripeError ? (
        <div
          className="mt-0 mb-4 flex w-full items-center text-red-700"
          data-testid="stripe-error-message"
          role="alert"
        >
          <svg
            className="mr-1 flex-shrink-0"
            width="30"
            height="30"
            focusable="false"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              fill="#d00"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            ></path>
          </svg>
          {stripeError}
        </div>
      ) : null}

      <FinalAgreement />
      <br />

      <SubmitButton
        data-testid="button-validate"
        disabled={isProcessing}
        isSubmitting={isProcessing}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          handleStripeSubmit();
        }}
      >
        {submitButtonText}
      </SubmitButton>
    </form>
  );
}
